import { Tabs } from "@exopengithub/exokit";
import { TabLink } from "components/tabs/TabLink";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, type To, useLocation } from "react-router";
import { useDocumentTitle } from "src/common/utils/hooks/useDocumentTitle";
import { HeaderLayout } from "src/routes/HeaderLayout.js";
import { QUERY_PARAM_GROUP } from "src/features/legalEntities/constants";
import { useLoadedLegalEntity } from "src/routes/routeDataHooks";

export const LegalEntityDetailLayout = () => {
	const legalEntity = useLoadedLegalEntity();
	const { t } = useTranslation();
	useDocumentTitle(legalEntity.name);

	// WHY still respected here: the /legal-entities list's "Group" row still
	// links in with ?group (see LegalEntitiesPage.tsx) — this only carries
	// that choice across tabs, it no longer offers a way to switch to it.
	const { search } = useLocation();
	const isGroupSelected = new URLSearchParams(search).has(QUERY_PARAM_GROUP);

	const buildTo = useCallback(
		(pathname: string): To => {
			return {
				pathname,
				search: isGroupSelected ? `?${QUERY_PARAM_GROUP}` : "",
			};
		},
		[isGroupSelected],
	);

	return (
		<HeaderLayout
			title={legalEntity.name}
			tabs={
				<Tabs>
					<TabLink to={buildTo("info")}>{t("Info")}</TabLink>
					<TabLink to={buildTo("accounts")}>{t("Accounts")}</TabLink>
					<TabLink to={buildTo("cost-centers")}>{t("Cost centers")}</TabLink>
					<TabLink to={buildTo("customers")}>{t("Customers")}</TabLink>
					<TabLink to={buildTo("suppliers")}>{t("Suppliers")}</TabLink>
					<TabLink to={buildTo("projects")}>{t("Projects")}</TabLink>
					<TabLink to={buildTo("products")}>{t("Products")}</TabLink>
					<TabLink to={buildTo("invoices")}>{t("Invoices")}</TabLink>
				</Tabs>
			}
		>
			<Outlet context={{ buildTo }} />
		</HeaderLayout>
	);
};
